<template>
  <div>
    <v-form
      :disabled="form.processing"
      @submit.prevent="submit"
    >
      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.auto_sort_cats"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Automatically sort categories')"
            :hint="$t('This setting will automatically sort categories and subcategories by popularity')"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.auto_sort_ques"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Automatically sort questions')"
            :hint="$t('This setting will automatically sort questions by popularity')"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.user_feedback"
            class="mt-0"
            dense
            color="primary"
            :label="$t('User feedback')"
            :hint="$t('Enabling user feedback will allow users to up or downvote the answer of your questions')"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.contact_info"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Show contact info block')"
            :hint="$t(`This will show a block containing the contact info entered under 'General'`)"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.contact_form"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Contact form')"
            :hint="$t('This setting will enable a contact form tab in the widget')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col
          cols="12"
          sm="6"
        >
          <color-picker
            v-model="form.primary_color"
            default-value="#d24919"
            :label="$t('Primary color')"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-autocomplete
            v-model="form.custom_toggle_icon_upload_id"
            :loading="form.processing"
            :error-messages="form.errors.custom_toggle_icon_upload_id"
            :items="images"
            :label="$t('Custom toggle icon')"
            item-text="title"
            item-value="id"
            outlined
            clearable
            :hint="$t('Select a custom toggle icon from the media library')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>{{ $t('Labels and texts') }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <div class="mb-3">
                  <p class="mb-0 subtitle-2">
                    {{ $t('Toggle button text') }}
                  </p>
                </div>

                <v-text-field
                  v-model="form.label"
                  dense
                  hide-details
                  outlined
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <div class="mb-3">
                  <p class="mb-0 subtitle-2">
                    {{ $t('FAQ tab title') }}
                  </p>
                </div>

                <v-text-field
                  v-model="form.faq_title"
                  dense
                  :hint="$t('Shown when the contact form is enabled')"
                  persistent-hint
                  outlined
                  :counter="25"
                  :maxlength="25"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <div class="mb-3">
                  <p class="mb-0 subtitle-2">
                    {{ $t('Contact form tab title') }}
                  </p>
                </div>

                <v-text-field
                  v-model="form.contact_form_title"
                  dense
                  :hint="$t('Shown when the contact form is enabled')"
                  persistent-hint
                  outlined
                  :counter="25"
                  :maxlength="25"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <portal to="faq-settings-layout-submit">
        <v-btn
          type="submit"
          color="primary"
          :loading="form.processing"
          :disabled="form.processing"
          @click="submit"
        >
          {{ $t('Save changes') }}
        </v-btn>
      </portal>
    </v-form>
  </div>
</template>

<script>
import Layout from '@/components/shared/TheLayout';
import TabsLayout from '@/components/faq/TabsLayout';
import SettingsLayout from '@/components/faq/settings/TabsLayout';
import unsavedFormWarningMixin from '@/mixins/faq/unsaved-form-warning';

export default {
  mixins: [ unsavedFormWarningMixin ],

  layout: (h, page) => h(Layout, {
    props: {
      noWrappingContainer: true,
      pageTitle: page.data.props.faq.default_translation.name
    }
  }, [h(TabsLayout, {
    props: {
      activeTab: 'settings',
      faq: page.data.props.faq
    }
  }, [h(SettingsLayout, {
      props: {
        faq: page.data.props.faq,
        activeTab: 'widget',
        showPreview: true,
        hideComponentSwitch: true,
      },
    }, [page])
  ])]),

  props: {
    faq: {
      type: Object,
      required: true
    },
    images: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data() {
    return {
      form: this.$inertia.form({
        user_feedback: this.faq.widget_user_feedback,
        primary_color: this.faq.widget_primary_color,
        auto_sort_ques: this.faq.widget_auto_sort_ques,
        auto_sort_cats: this.faq.widget_auto_sort_cats,
        label: this.faq.default_translation.widget_label,
        contact_info: this.faq.widget_contact_info,
        contact_form: this.faq.widget_contact_form,
        contact_form_title: this.faq.default_translation.widget_contact_form_title,
        faq_title: this.faq.default_translation.widget_faq_title,
        custom_toggle_icon_upload_id: this.faq.widget_custom_toggle_icon_upload_id,
      }),
      previewKey: 0
    }
  },

  created() {
    this.$root.preview.faq.component = 'widget';

    window.onbeforeunload = () => this.form.isDirty ? '' : null;
  },

  methods: {
    submit() {
      const url = this.route('faq-settings.widget.update', this.faq.id);
      this.form.patch(url.toString(), {
        onSuccess: () => {
          this.$root.refreshFaqPreview();
        }
      });
    },
  }
}
</script>